import React from 'react';
import FrameworkSummaryCard from './FrameworkSummaryCard';

/**
 * FrameworkCompletionModal - WOW completion experience when user finishes a framework
 * 
 * Displays the formula results in a structured, visually stunning way
 * that matches the coach's framework structure.
 * 
 * Supports both v1 (legacy) and v2 (schema-aware) completion data formats.
 */
const FrameworkCompletionModal = ({
  isOpen,
  onClose,
  frameworkName,
  completionData,
  conversationContext,
  onSave,
  onExport,
  onDelete,
  sessionId,
  isViewingMode = false
}) => {
  if (!isOpen) return null;

  // Delete handler
  const handleDelete = async () => {
    if (!sessionId || !onDelete) return;

    if (window.confirm('Are you sure you want to delete this framework session? This action cannot be undone.')) {
      try {
        await onDelete(sessionId);
        onClose();
      } catch (error) {
        console.error('Failed to delete framework session:', error);
        alert('Failed to delete framework session. Please try again.');
      }
    }
  };

  // Check if this is a v2 completion (has summary card)
  const isV2 = completionData?.isV2 || completionData?.summaryCard;
  
  // If v2, render the new summary card instead
  if (isV2 && completionData?.summaryCard) {
    return (
      <div className="framework-completion-overlay" onClick={onClose}>
        <div className="framework-completion-modal-v2" onClick={e => e.stopPropagation()}>
          {/* Header with celebration */}
          <div className="completion-header-v2">
            <div className="celebration-icon">{isViewingMode ? '🏆' : '🎉'}</div>
            <h2>{isViewingMode ? frameworkName : `${frameworkName} Complete!`}</h2>
            <p className="completion-subtitle">
              {isViewingMode ? 'Your completed framework summary' : 'You\'ve successfully worked through all stages'}
            </p>
          </div>

          {/* V2 Summary Card */}
          <div className="summary-card-container">
            <FrameworkSummaryCard
              summaryCard={completionData.summaryCard}
              onExport={onExport}
              onContinue={onClose}
            />
          </div>

          {/* Action Buttons */}
          <div className="completion-actions-v2">
            {isViewingMode && sessionId && onDelete && (
              <button className="action-btn danger" onClick={handleDelete}>
                🗑️ Delete
              </button>
            )}
            <button className="action-btn secondary" onClick={onClose}>
              {isViewingMode ? 'Close' : 'Continue Chat'}
            </button>
            {!isViewingMode && (
              <button className="action-btn primary" onClick={onSave}>
                💾 Save to Profile
              </button>
            )}
          </div>
          
          <style jsx>{`
            .framework-completion-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(30, 58, 138, 0.6);
              backdrop-filter: blur(16px) saturate(180%);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10000;
              animation: overlay-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @keyframes overlay-fade {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            .framework-completion-modal-v2 {
              background: linear-gradient(145deg, #1a1a2e 0%, #16213e 100%);
              border-radius: 24px;
              border: 1px solid rgba(78, 205, 196, 0.2);
              max-width: 650px;
              width: 95%;
              max-height: 92vh;
              overflow-y: auto;
              box-shadow: 0 16px 64px rgba(0, 0, 0, 0.5);
              animation: modal-entrance 0.5s cubic-bezier(0.16, 1, 0.3, 1);
            }
            
            @keyframes modal-entrance {
              from {
                opacity: 0;
                transform: translateY(32px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
            
            .completion-header-v2 {
              background: linear-gradient(135deg, #4ECDC4 0%, #44a08d 100%);
              padding: 32px;
              text-align: center;
              border-radius: 24px 24px 0 0;
              color: white;
            }
            
            .celebration-icon {
              font-size: 56px;
              animation: celebrate 1.2s ease-in-out infinite;
            }
            
            @keyframes celebrate {
              0%, 100% { transform: translateY(0) rotate(-8deg) scale(1); }
              50% { transform: translateY(-12px) rotate(8deg) scale(1.1); }
            }
            
            .completion-header-v2 h2 {
              margin: 16px 0 8px;
              font-size: 28px;
              font-weight: 700;
            }
            
            .completion-subtitle {
              opacity: 0.9;
              font-size: 15px;
              margin: 0;
            }
            
            .summary-card-container {
              padding: 0 16px;
            }
            
            .completion-actions-v2 {
              padding: 20px 24px;
              display: flex;
              gap: 12px;
              justify-content: flex-end;
              border-top: 1px solid rgba(78, 205, 196, 0.1);
            }
            
            .action-btn {
              padding: 14px 24px;
              border-radius: 12px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s ease;
              border: none;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            
            .action-btn.primary {
              background: linear-gradient(135deg, #4ECDC4 0%, #44a08d 100%);
              color: white;
            }
            
            .action-btn.primary:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 16px rgba(78, 205, 196, 0.4);
            }
            
            .action-btn.secondary {
              background: rgba(78, 205, 196, 0.1);
              color: #4ECDC4;
              border: 1px solid rgba(78, 205, 196, 0.3);
            }

            .action-btn.secondary:hover {
              background: rgba(78, 205, 196, 0.2);
            }

            .action-btn.danger {
              background: rgba(239, 68, 68, 0.1);
              color: #EF4444;
              border: 1px solid rgba(239, 68, 68, 0.3);
            }

            .action-btn.danger:hover {
              background: rgba(239, 68, 68, 0.2);
              border-color: rgba(239, 68, 68, 0.5);
            }
          `}</style>
        </div>
      </div>
    );
  }
  
  // ============================================
  // V1 LEGACY COMPLETION RENDERING
  // ============================================

  // Combine all context sources
  const ctx = {
    ...conversationContext,
    ...(completionData?.context || {}),
    ...(completionData?.collected || {})
  };

  // Helper to format snake_case keys to Title Case
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper to format values - remove underscores, clean up
  const formatValue = (value) => {
    if (!value) return "TBD";
    const str = String(value);
    // Replace underscores with spaces and capitalize first letter
    return str
      .replace(/_/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper to extract first non-empty value from possible keys
  function extractValue(context, keys) {
    for (const key of keys) {
      if (context[key] && String(context[key]).trim() && context[key] !== "Not specified") {
        return formatValue(context[key]);
      }
    }
    return null;
  }

  // Extract SVF-specific formula components
  const formulaComponents = {
    // WHO - Perfect Fit Customer
    who: formatValue(ctx.customer_type || ctx.stage_1_answers?.join(', ')) || "Your ideal customer",

    // DEAL SIZE
    dealSize: extractValue(ctx, ['pricing', 'deal_size', 'target_deal_size', 'price_point']) || "TBD",

    // SALES CYCLE
    salesCycle: extractValue(ctx, ['sales_cycle', 'length_of_sales_cycle']) || "TBD",

    // ACCESS - How they find you
    access: formatValue(ctx.customer_source || ctx.market_access_influence) || extractValue(ctx, ['channel', 'acquisition_source']) || "TBD",

    // PAIN - What hurts
    pain: formatValue(ctx.pain_points) || extractValue(ctx, ['challenge', 'pain_point']) || "TBD",

    // CLOSE CRITERIA - What makes them buy
    criteria: extractValue(ctx, ['close_criteria', 'criteria']) || formatValue(ctx.stage_3_answers?.join(', ')) || "TBD",

    // SOLUTION
    solution: formatValue(ctx.solution_benefit || ctx.stage_4_answers?.[0]) || "Your solution"
  };

  // Get all stage answers for "What You Shared"
  const allAnswers = [];
  for (let i = 1; i <= 5; i++) {
    const stageKey = `stage_${i}_answers`;
    if (ctx[stageKey] && Array.isArray(ctx[stageKey])) {
      allAnswers.push(...ctx[stageKey]);
    }
  }
  
  // Also add any direct context values
  Object.entries(ctx).forEach(([key, value]) => {
    if (typeof value === 'string' && value.length > 5 && value.length < 150 && !key.includes('stage_')) {
      if (!allAnswers.includes(value)) {
        allAnswers.push(value);
      }
    }
  });

  // Deduplicate and clean
  const uniqueAnswers = [...new Set(allAnswers)]
    .filter(a => a && a.length > 3 && a.length < 200)
    .slice(-8);

  // Generate action items from expected outputs or defaults
  const actionItems = completionData?.expected_outputs?.slice(0, 4) || [
    "Target Deal Size: Clear revenue goal per customer",
    "Access Strategy: Where to find your best customers",
    "Close Criteria: What makes a customer a perfect fit (4 C's)",
    "Predictable Sales Cycle: Timeline from first contact to close"
  ];

  return (
    <div className="framework-completion-overlay" onClick={onClose}>
      <div className="framework-completion-modal" onClick={e => e.stopPropagation()}>
        {/* Header with celebration */}
        <div className="completion-header">
          <div className="celebration-icon">{isViewingMode ? '🏆' : '🎉'}</div>
          <h2>{isViewingMode ? frameworkName : `${frameworkName} Complete!`}</h2>
          <p className="completion-subtitle">
            {isViewingMode ? 'Your completed framework summary' : 'You\'ve successfully worked through all stages'}
          </p>
        </div>

        {/* YOUR PERFECT FIT CUSTOMER - Formula Display */}
        <div className="completion-section formula-section">
          <h3>🎯 YOUR PERFECT FIT CUSTOMER</h3>
          
          <div className="formula-display">
            {/* Main formula row */}
            <div className="formula-equation">
              <div className="formula-item highlight">
                <span className="formula-label">WHO</span>
                <span className="formula-value">{formulaComponents.who}</span>
              </div>
              <span className="formula-operator">×</span>
              <div className="formula-item">
                <span className="formula-label">DEAL SIZE</span>
                <span className="formula-value">{formulaComponents.dealSize}</span>
              </div>
              <span className="formula-operator">×</span>
              <div className="formula-item">
                <span className="formula-label">ACCESS</span>
                <span className="formula-value">{formulaComponents.access}</span>
              </div>
            </div>

            {/* Second row - more details */}
            <div className="formula-details">
              <div className="detail-card pain">
                <span className="detail-icon">🔥</span>
                <div className="detail-content">
                  <span className="detail-label">THEIR PAIN</span>
                  <span className="detail-value">{formulaComponents.pain}</span>
                </div>
              </div>
              <div className="detail-card cycle">
                <span className="detail-icon">⏱️</span>
                <div className="detail-content">
                  <span className="detail-label">SALES CYCLE</span>
                  <span className="detail-value">{formulaComponents.salesCycle}</span>
                </div>
              </div>
              <div className="detail-card solution">
                <span className="detail-icon">✨</span>
                <div className="detail-content">
                  <span className="detail-label">YOUR SOLUTION</span>
                  <span className="detail-value">{formulaComponents.solution}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What You Shared - Collapsible */}
        {uniqueAnswers.length > 0 && (
          <div className="completion-section answers-section">
            <h3>💬 WHAT YOU SHARED ({uniqueAnswers.length} insights)</h3>
            <div className="answers-scroll">
              {uniqueAnswers.map((answer, idx) => (
                <div key={idx} className="answer-chip">
                  "{answer}"
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="completion-section">
          <h3>📋 NEXT STEPS</h3>
          <ul className="action-items">
            {actionItems.map((item, idx) => (
              <li key={idx} className="action-item">
                <span className="action-checkbox">☐</span>
                <span className="action-text">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="completion-actions">
          {isViewingMode && sessionId && onDelete && (
            <button className="action-btn danger" onClick={handleDelete}>
              🗑️ Delete
            </button>
          )}
          <button className="action-btn secondary" onClick={onClose}>
            {isViewingMode ? 'Close' : 'Continue Chat'}
          </button>
          {!isViewingMode && (
            <button className="action-btn primary" onClick={onSave}>
              💾 Save to Profile
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        /* Apple-Premium Glass Morphism Overlay */
        .framework-completion-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(30, 58, 138, 0.6); /* Navy overlay */
          backdrop-filter: blur(16px) saturate(180%);
          -webkit-backdrop-filter: blur(16px) saturate(180%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: mm-overlay-fade 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes mm-overlay-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Apple-Premium Modal Card */
        .framework-completion-modal {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border-radius: 28px;
          border: 2px solid rgba(139, 92, 246, 0.15);
          max-width: 600px;
          width: 95%;
          max-height: 92vh;
          overflow-y: auto;
          box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.08),
            0 16px 64px rgba(139, 92, 246, 0.15);
          animation: mm-modal-entrance 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          -webkit-overflow-scrolling: touch;
        }

        @keyframes mm-modal-entrance {
          from {
            opacity: 0;
            transform: translateY(32px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Apple-Premium Header - Purple Gradient */
        .completion-header {
          background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%);
          padding: 40px 32px;
          text-align: center;
          border-radius: 26px 26px 0 0;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .completion-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 100%);
          pointer-events: none;
        }

        /* Celebration Icon with Premium Animation */
        .celebration-icon {
          font-size: 64px;
          animation: mm-celebrate 1.2s ease-in-out infinite;
          filter: drop-shadow(0 8px 16px rgba(0,0,0,0.25));
          position: relative;
          z-index: 1;
        }

        @keyframes mm-celebrate {
          0%, 100% {
            transform: translateY(0) rotate(-8deg) scale(1);
          }
          25% {
            transform: translateY(-16px) rotate(8deg) scale(1.1);
          }
          50% {
            transform: translateY(-8px) rotate(-8deg) scale(1.05);
          }
          75% {
            transform: translateY(-12px) rotate(4deg) scale(1.08);
          }
        }

        .completion-header h2 {
          margin: 20px 0 8px;
          font-size: 32px;
          font-weight: 800;
          letter-spacing: -0.02em;
          text-shadow: 0 2px 8px rgba(0,0,0,0.15);
          position: relative;
          z-index: 1;
        }

        .completion-subtitle {
          opacity: 0.95;
          font-size: 16px;
          margin: 0;
          font-weight: 500;
          position: relative;
          z-index: 1;
        }

        /* Glass Morphism Sections */
        .completion-section {
          padding: 28px 32px;
          border-bottom: 1px solid rgba(139, 92, 246, 0.08);
          position: relative;
        }

        .completion-section h3 {
          font-size: 13px;
          font-weight: 700;
          color: #8B5CF6;
          margin: 0 0 20px;
          text-transform: uppercase;
          letter-spacing: 1.2px;
        }

        /* Formula Display - Apple-Premium Purple Theme */
        .formula-section {
          background: linear-gradient(180deg,
            rgba(139, 92, 246, 0.05) 0%,
            rgba(255, 255, 255, 0.98) 100%);
          backdrop-filter: blur(20px);
        }

        .formula-display {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .formula-equation {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        /* Apple-Premium Formula Cards */
        .formula-item {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          border: 2px solid rgba(139, 92, 246, 0.12);
          border-radius: 16px;
          padding: 16px 20px;
          text-align: center;
          min-width: 100px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 2px 8px rgba(139, 92, 246, 0.06);
        }

        .formula-item:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(139, 92, 246, 0.15);
          border-color: rgba(139, 92, 246, 0.25);
        }

        /* Highlighted Purple Gradient Card */
        .formula-item.highlight {
          background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%);
          border-color: transparent;
          color: white;
          transform: scale(1.08);
          box-shadow:
            0 8px 20px rgba(139, 92, 246, 0.35),
            0 4px 12px rgba(124, 58, 237, 0.25);
        }

        .formula-item.highlight .formula-label {
          color: rgba(255,255,255,0.95);
        }

        .formula-label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }

        .formula-value {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          line-height: 1.3;
        }

        .formula-item.highlight .formula-value {
          color: white;
        }

        /* Purple Formula Operator */
        .formula-operator {
          font-size: 24px;
          font-weight: 800;
          color: #8B5CF6;
          text-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
        }

        /* Detail Cards Grid */
        .formula-details {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        /* Apple-Premium Detail Cards */
        .detail-card {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 16px;
          border: 2px solid rgba(139, 92, 246, 0.08);
          display: flex;
          align-items: flex-start;
          gap: 12px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .detail-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(139, 92, 246, 0.12);
          border-color: rgba(139, 92, 246, 0.15);
        }

        .detail-card.pain {
          border-left: 4px solid #EF4444;
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%);
        }

        .detail-card.cycle {
          border-left: 4px solid #3B82F6;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%);
        }

        .detail-card.solution {
          border-left: 4px solid #10B981;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(255, 255, 255, 0.9) 100%);
        }

        .detail-icon {
          font-size: 18px;
        }

        .detail-content {
          display: flex;
          flex-direction: column;
          gap: 3px;
          flex: 1;
          min-width: 0;
        }

        .detail-label {
          font-size: 9px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .detail-value {
          font-size: 12px;
          font-weight: 600;
          color: #334155;
          line-height: 1.3;
          word-break: break-word;
        }

        /* Answers Section */
        .answers-section {
          background: #f8fafc;
        }

        .answers-scroll {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          max-height: 120px;
          overflow-y: auto;
        }

        .answer-chip {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 8px 14px;
          font-size: 12px;
          color: #475569;
          font-style: italic;
        }

        /* Action Items */
        .action-items {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .action-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 14px;
          background: #f8fafc;
          border-radius: 10px;
          margin-bottom: 8px;
          font-size: 13px;
          color: #334155;
          line-height: 1.4;
        }

        .action-checkbox {
          color: #10b981;
          font-size: 16px;
          flex-shrink: 0;
        }

        /* Action Buttons */
        .completion-actions {
          padding: 24px 28px;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        /* Apple-Premium Action Buttons */
        .action-btn {
          padding: 16px 28px;
          border-radius: 16px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        /* Purple Gradient Primary Button */
        .action-btn.primary {
          background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%);
          color: white;
          box-shadow:
            0 4px 16px rgba(139, 92, 246, 0.35),
            0 2px 8px rgba(124, 58, 237, 0.25);
        }

        .action-btn.primary:hover {
          transform: translateY(-3px);
          box-shadow:
            0 8px 24px rgba(139, 92, 246, 0.45),
            0 4px 12px rgba(124, 58, 237, 0.35);
        }

        .action-btn.primary:active {
          transform: translateY(-1px);
          box-shadow:
            0 4px 12px rgba(139, 92, 246, 0.3),
            0 2px 6px rgba(124, 58, 237, 0.2);
        }

        /* Glass Morphism Secondary Button */
        .action-btn.secondary {
          background: rgba(139, 92, 246, 0.08);
          backdrop-filter: blur(8px);
          color: #7C3AED;
          border: 2px solid rgba(139, 92, 246, 0.15);
        }

        .action-btn.secondary:hover {
          background: rgba(139, 92, 246, 0.12);
          border-color: rgba(139, 92, 246, 0.25);
          transform: translateY(-2px);
        }

        /* Danger Button for Delete */
        .action-btn.danger {
          background: rgba(239, 68, 68, 0.08);
          backdrop-filter: blur(8px);
          color: #EF4444;
          border: 2px solid rgba(239, 68, 68, 0.15);
        }

        .action-btn.danger:hover {
          background: rgba(239, 68, 68, 0.15);
          border-color: rgba(239, 68, 68, 0.3);
          transform: translateY(-2px);
        }

        @media (max-width: 480px) {
          .formula-equation {
            flex-direction: column;
          }
          
          .formula-operator {
            display: none;
          }

          .formula-details {
            grid-template-columns: 1fr;
          }
          
          .completion-actions {
            flex-direction: column;
          }
          
          .action-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default FrameworkCompletionModal;
